import { memo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { PRODUCT_TAG_CODES } from "app/constants";
import UnbeatableOfferMiddleIcon from "app/pages/.shared/static/icons/UnbeatableOfferMiddleIcon";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS } from "app/constants";
import "./UnbeatableOfferTag.scss";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { isBrandWithAccount } from "app/utils/utils";
import { FireFilledIcon, SquareLockFilledIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";

const UnbeatableOfferTag = ({ type, className, shorten = false }) => {
	const { brand, isUserConnected, strategy } = useContext(AppGlobalsContext);
	const { track } = useAnalytics();
	const isBrandAF = brand === BRANDS.AF;
	const isBrandTO = brand === BRANDS.TO;
	const isBrandEK = brand === BRANDS.EK;

	const handleClick = useCallback(() => {
		track("unbeatable-offer-tag-rage-click", {
			type,
		});
	}, [type]);

	const shouldDisplayMemberOnlyTag = isBrandWithAccount({ brand, strategy }) && !isUserConnected;

	return (
		<div
			className={classNames({
				...(className && { [className]: true }),
				"unbeatable-offer-tag": true,
				"unbeatable-offer-tag--shorten": shorten,
				"unbeatable-offer-tag--last-day": type === PRODUCT_TAG_CODES.LAST_DAY,
				"unbeatable-offer-tag--limited-time": type === PRODUCT_TAG_CODES.LIMITED_TIME,
				"unbeatable-offer-tag--unconnected": shouldDisplayMemberOnlyTag,
				"unbeatable-offer-tag--unconnected-af": shouldDisplayMemberOnlyTag && isBrandAF,
				"unbeatable-offer-tag--unconnected-to": shouldDisplayMemberOnlyTag && isBrandTO,
				"unbeatable-offer-tag--unconnected-ek": shouldDisplayMemberOnlyTag && isBrandEK,
			})}
			data-testid="unbeatable-offer-tag"
			onClick={handleClick}
		>
			{shouldDisplayMemberOnlyTag ? (
				<SquareLockFilledIcon className="unbeatable-offer-tag__icon" size={ICON_SIZES.XS} />
			) : (
				<FireFilledIcon className="unbeatable-offer-tag__icon" size={ICON_SIZES.XS} />
			)}
			{shorten ? (
				<Typography
					variant={TYPOGRAPHY_VARIANTS.SMALL}
					className="unbeatable-offer-tag__label"
					isBold
				>
					<FormattedMessage
						id={
							shouldDisplayMemberOnlyTag
								? "product.unbeatable.offer.label"
								: `product.unbeatable.offer.${type}`
						}
					/>
				</Typography>
			) : (
				<>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.SMALL}
						className="unbeatable-offer-tag__label"
						isBold
					>
						<FormattedMessage id="product.unbeatable.offer.label" />
					</Typography>
					<div className="unbeatable-offer-tag__type">
						<UnbeatableOfferMiddleIcon className="unbeatable-offer-tag__middle-icon" />
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							className="unbeatable-offer-tag__type-label"
							isBold
						>
							<FormattedMessage
								id={
									shouldDisplayMemberOnlyTag
										? "product.unbeatable.offer.access.limited.label"
										: `product.unbeatable.offer.${type}`
								}
							/>
						</Typography>
					</div>
				</>
			)}
		</div>
	);
};

UnbeatableOfferTag.propTypes = {
	className: PropTypes.string,
	shorten: PropTypes.bool,
	type: PropTypes.oneOf([PRODUCT_TAG_CODES.LIMITED_TIME, PRODUCT_TAG_CODES.LAST_DAY]),
};

export default memo(UnbeatableOfferTag);
